import React from 'react';
import { TestCountVariant } from '@/components/raytd/test-count-pill';
import AuthorBadge from '@/components/raytd/user-avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger } from '@/components/ui/tooltip';
import { Assessment } from '@app.raytd.com/store';
import { TooltipContent } from '@radix-ui/react-tooltip';
import { useReportViewContext } from '../report-viewer-context';
import { useTestTileContext } from './context';
import DebugInfo from './debug-info';
import ExclusionToggle from './exclusion-toggle';
import FastFillAcceptedToggle from './fast-fill-toggle';
import StatusBadges from './save-status-badges';
import { useTestSuiteTypeColor } from '@/app/features/reports/ratingHelpers';
import { Pencil, MessageSquare } from 'lucide-react';
import { TestErrorIcons } from '@/app/features/reports/components/test-error-icons';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { featureFlags } from '@/config/features';
import CommentsSection from './comments/comments-section';


const AssessmentBadge: React.FC<{ assessment: Assessment }> = ({ assessment }) => {
    return (
        <Tooltip>
            <TooltipTrigger>
                <div className="text-xs bg-white/75 text-zinc-500 px-2 py-1 lg:max-w-48 max-w-24 rounded-sm truncate">
                    {assessment?.title}
                </div>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white p-2 rounded-md text-sm">
                {assessment?.title}
            </TooltipContent>
        </Tooltip>
    );
};

type EditToolsProps = {
    onEdit?: (id: number) => void;
    assessment: Assessment;
}

const TestTileEditTools: React.FC<EditToolsProps> = ({ onEdit, assessment }) => {

    const { item, hasFastFillErrors, hasUnmatchedSegments, exclusionReason, hasMissingData, isCommentsOpen, setIsCommentsOpen } = useTestTileContext();
    const { isEditMode, report, viewId } = useReportViewContext();

    // Hide exclusion toggle in inspection context (when report.id is null)
    const showExclusionToggle = report?.id !== null;
    const isManuallyExcluded = item.switches?.include !== true;
    const hasStatusError = ['draft', 'qa', 'archived'].includes(item.status.toLowerCase());
    const unresolvedCount = item.unresolved_comments_count ?? 0;
    const isPrintMode = viewId === 'print';
    const commentsEnabled = featureFlags.testItemComments();

    const handleCommentClick = () => {
        setIsCommentsOpen(prev => !prev);
    };

    if (!isEditMode) {
        return null;
    }

    return (
        <div className="pl-4 pr-2 py-2" style={{ backgroundColor: useTestSuiteTypeColor(item.ratingType, 10) }}>
            <div className="flex flex-row justify-between items-center gap-2">
                <div className="flex items-center space-x-2">
                    <Button
                        variant="ghost"
                        size='sm'
                        className="text-zinc-500 bg-transparent rounded-md py-1 px-2"
                        aria-label="Edit Test Item"
                        onClick={() => onEdit?.(item.assessmentTestId)}
                    >
                        <Pencil className="h-5 w-5" />
                    </Button>
                    {showExclusionToggle && 
                    <ExclusionToggle 
                        enableAlways={true}
                        className="bg-white/75 rounded-md py-1 px-2"
                    />}
                    <FastFillAcceptedToggle
                        visible={hasUnmatchedSegments}
                        className="bg-white/75 rounded-md py-1 px-2"
                    />
                    {commentsEnabled && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size='sm'
                                className={cn(
                                    "text-zinc-500 bg-transparent rounded-md py-1 px-2 relative",
                                    unresolvedCount > 0 && "text-orange-600"
                                )}
                                aria-label={`Comments (${unresolvedCount} unresolved)`}
                                onClick={handleCommentClick}
                            >
                                <MessageSquare className="h-5 w-5" />
                                {unresolvedCount > 0 && (
                                    <Badge
                                        variant="destructive"
                                        className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                                    >
                                        {unresolvedCount > 99 ? '99+' : unresolvedCount}
                                    </Badge>
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-black text-white p-2 rounded-md text-sm">
                            {unresolvedCount > 0
                                ? `${unresolvedCount} unresolved comment${unresolvedCount !== 1 ? 's' : ''}`
                                : 'No unresolved comments'}
                        </TooltipContent>
                    </Tooltip>
                    )}
                    {process.env.NODE_ENV === 'development' && (
                        <DebugInfo
                            exclusionReason={item.exclusionReason}
                            status={item.status}
                            id={item.assessmentTestId}
                        />
                    )}
                </div>
                <div className="flex flex-row items-center gap-2 justify-end">
                    <AssessmentBadge assessment={assessment} />
                    <AuthorBadge item={item.author} />
                    <StatusBadges status={item.status as TestCountVariant} />
                    <TestErrorIcons 
                        size="sm"
                        included={!isManuallyExcluded}
                        fastFillError={hasFastFillErrors}
                        missingError={hasMissingData}
                        statusError={hasStatusError}
                    />
                </div>
            </div>
            {commentsEnabled && isEditMode && !isPrintMode && isCommentsOpen && (
                <div className="mt-2">
                    {/* Inline comments panel inside the edit toolbar */}
                    <CommentsSection testItemId={item.assessmentTestId} />
                </div>
            )}
        </div>
    )
}

TestTileEditTools.displayName = 'TestTileEditTools';

export default TestTileEditTools;